import {
  AutomationsPresentationV1,
  AutomationsPresentationV1Edge
} from 'Automations/automationEditor'
import {
  dedupeEdges,
  getStartNode,
  parseNodeHandleId,
  pruneOrphanedEdges
} from 'Automations/nodeDefinitions/helpers'
import {
  NodeType,
  ResolvedNodeDefinitions
} from 'Automations/nodeDefinitions/types'
import {
  BuildResult,
  BuildStructureFn,
  IntermediateNode
} from '../../presentationToConductorWorkflow.types'
import { getBaseNodeIds, resolveJoinNodes } from './resolveJoinNodes'
import { sortEdgesByHandleMapping } from './sortEdgesByHandleMapping'

export const transformNodeGraphToIntermediateNodeArray =
  (definitions: ResolvedNodeDefinitions) =>
  (presentation: AutomationsPresentationV1): IntermediateNode[] => {
    const { nodes } = presentation

    /**
     * The branch/fork decision below counts edges, and runs before a missing
     * target is discovered — so an orphan would fork the graph, and a
     * connection stored twice would fork it into two identical branches.
     * Canvases saved before we started de-duplicating still hold one, so clean
     * both up here rather than trusting whatever we were handed.
     */
    const edges = dedupeEdges(pruneOrphanedEdges(presentation))

    const startNode = getStartNode(presentation, definitions)
    if (!startNode) throw new Error('valid start node is missing')

    const outgoingEdges = groupOutgoingEdgesForNodes(edges)
    const incomingSources = groupIncomingSourceNodeIds(edges)
    const joinNodeIds = getJoinNodeIds(incomingSources, nodes)

    const buildStructure: BuildStructureFn = ({
      nodeId,
      visited,
      branchPath = '',
      isInsideParallelFork = false
    }) => {
      const node = nodes.find(n => n.id === nodeId)

      if (!node || visited.has(nodeId)) {
        return { intermediateNodes: [], skippedJoinNodes: new Set<string>() }
      }

      /**
       * Add this node to `skippedJoinNodes` so we can figure out if it needs
       * to be placed after the join because multiple branches lead to it
       */
      if (joinNodeIds.has(nodeId) && isInsideParallelFork) {
        return { intermediateNodes: [], skippedJoinNodes: new Set([nodeId]) }
      }

      /**
       * When a node is used by multiple branches, we duplicate them.
       * To ensure that the taskReferenceName is unique, append a suffix to the
       * original ID
       */
      const nodeWithUniqueBranchId = branchPath
        ? { ...node, id: `${node.id}__${branchPath}` }
        : node

      const newVisited = new Set([...visited, nodeId])
      const unsortedNodeEdges = outgoingEdges.get(nodeId) ?? []

      const definition = definitions[node.type as NodeType]
      if (!definition) throw new Error('definition not found')

      const handleMapping = definition.handleMapping?.({ config: node.data })

      if (unsortedNodeEdges.length === 0) {
        return {
          intermediateNodes: [{ node: nodeWithUniqueBranchId, branches: [] }],
          skippedJoinNodes: new Set()
        }
      }

      if (unsortedNodeEdges.length === 1) {
        const next = buildStructure({
          nodeId: unsortedNodeEdges[0].target,
          visited: newVisited,
          branchPath,
          isInsideParallelFork
        })

        return {
          intermediateNodes: [
            { node: nodeWithUniqueBranchId, branches: [] },
            ...next.intermediateNodes
          ],
          skippedJoinNodes: next.skippedJoinNodes
        }
      }

      const sortedNodeEdges = sortEdgesByHandleMapping({
        unsortedNodeEdges,
        handleMapping
      })

      /**
       * Group edges by handle to detect same-handle parallelism within
       * multi-handle nodes (e.g. SWITCH with multiple edges on one handle).
       */
      const edgesByHandle = handleMapping
        ? groupEdgesByHandle(sortedNodeEdges)
        : new Map<string, AutomationsPresentationV1Edge[]>()

      const branchResults = sortedNodeEdges.map((edge, i) => {
        const childPath = branchPath ? `${branchPath}-${i}` : `${i}`

        const handle =
          parseNodeHandleId(edge.sourceHandle ?? '').handleMapping ?? 'default'
        const isParallelInGroup =
          handleMapping && (edgesByHandle.get(handle)?.length ?? 0) > 1

        /**
         * Nodes coming off of a single-handle node always need to be run in parallel.
         * For multi-handle nodes, edges in handle groups with >1 edge are also parallel.
         */
        const isChildInsideParallelFork = handleMapping
          ? isInsideParallelFork || isParallelInGroup
          : true

        return buildStructure({
          nodeId: edge.target,
          visited: newVisited,
          branchPath: childPath,
          isInsideParallelFork: isChildInsideParallelFork
        })
      })

      const branches = branchResults.map(r => r.intermediateNodes)
      const allSkipped = collectSkippedJoinNodeIds(branchResults)
      const branchHandles = extractBranchHandles(sortedNodeEdges)

      /**
       * Per-group join resolution: for handle groups with >1 edge, resolve
       * join nodes within the group's scope so continuations stay inside the
       * decision case rather than bubbling up to the top-level SWITCH.
       */
      const {
        groupContinuations,
        groupResolvedIds,
        groupRemainingSkipped,
        groupVisitedNodes
      } = handleMapping
        ? resolvePerGroupJoinNodes({
            edgesByHandle,
            sortedNodeEdges,
            branchResults,
            branches,
            nodeId,
            newVisited,
            branchPath,
            isInsideParallelFork,
            outgoingEdges,
            joinNodeIds,
            incomingSources,
            buildStructure
          })
        : {
            groupContinuations: {},
            groupResolvedIds: new Set<string>(),
            groupRemainingSkipped: new Set<string>(),
            groupVisitedNodes: new Set<string>()
          }

      /**
       * Top-level resolution: resolve remaining skipped nodes that weren't
       * handled by per-group resolution.
       */
      const topLevelSkipped = new Set(
        [...allSkipped, ...groupRemainingSkipped].filter(
          id => !groupResolvedIds.has(id)
        )
      )

      const { continuation, remainingSkipped } = resolveJoinNodes({
        forkNodeId: nodeId,
        branchTargets: new Set(sortedNodeEdges.map(e => e.target)),
        skippedNodeIds: topLevelSkipped,
        visitedSoFar: new Set([...newVisited, ...groupVisitedNodes]),
        branchNodes: branches,
        branchPath,
        isInsideParallelFork,
        outgoingEdges,
        joinNodeIds,
        incomingSources,
        buildStructure
      })

      const hasGroupContinuations = Object.keys(groupContinuations).length > 0

      return {
        intermediateNodes: [
          {
            node: nodeWithUniqueBranchId,
            branches,
            branchHandles,
            ...(hasGroupContinuations && { groupContinuations })
          },
          ...continuation
        ],
        skippedJoinNodes: remainingSkipped
      }
    }

    return buildStructure({ nodeId: startNode.id, visited: new Set() })
      .intermediateNodes
  }

const groupOutgoingEdgesForNodes = (edges: AutomationsPresentationV1Edge[]) => {
  return edges.reduce<Map<string, AutomationsPresentationV1['edges']>>(
    (acc, edge) => {
      const existing = acc.get(edge.source) ?? []
      return acc.set(edge.source, [...existing, edge])
    },
    new Map()
  )
}

const groupIncomingSourceNodeIds = (
  edges: AutomationsPresentationV1Edge[]
): Map<string, Set<string>> => {
  return edges.reduce<Map<string, Set<string>>>((acc, edge) => {
    const sources = acc.get(edge.target) ?? new Set()
    return acc.set(edge.target, sources.add(edge.source))
  }, new Map())
}

const getJoinNodeIds = (
  incomingSources: Map<string, Set<string>>,
  nodes: AutomationsPresentationV1['nodes']
): Set<string> => {
  const terminalNodeIds = new Set(
    nodes.filter(n => n.type === 'END').map(n => n.id)
  )

  return new Set(
    [...incomingSources.entries()]
      .filter(
        ([target, sources]) => sources.size > 1 && !terminalNodeIds.has(target)
      )
      .map(([target]) => target)
  )
}

const collectSkippedJoinNodeIds = (
  results: { skippedJoinNodes: Set<string> }[]
): Set<string> => {
  return new Set(results.flatMap(r => [...r.skippedJoinNodes]))
}

const groupEdgesByHandle = (
  edges: AutomationsPresentationV1Edge[]
): Map<string, AutomationsPresentationV1Edge[]> => {
  return edges.reduce<Map<string, AutomationsPresentationV1Edge[]>>(
    (acc, edge) => {
      const handle =
        parseNodeHandleId(edge.sourceHandle ?? '').handleMapping ?? 'default'
      const existing = acc.get(handle) ?? []
      return acc.set(handle, [...existing, edge])
    },
    new Map()
  )
}

const extractBranchHandles = (
  edges: AutomationsPresentationV1Edge[]
): string[] => {
  return edges.map(edge => {
    return parseNodeHandleId(edge.sourceHandle ?? '').handleMapping ?? 'default'
  })
}

type PerGroupJoinResult = {
  groupContinuations: Record<string, IntermediateNode[]>
  groupResolvedIds: Set<string>
  groupRemainingSkipped: Set<string>
  groupVisitedNodes: Set<string>
}

const resolvePerGroupJoinNodes = ({
  edgesByHandle,
  sortedNodeEdges,
  branchResults,
  branches,
  nodeId,
  newVisited,
  branchPath,
  isInsideParallelFork,
  outgoingEdges,
  joinNodeIds,
  incomingSources,
  buildStructure
}: {
  edgesByHandle: Map<string, AutomationsPresentationV1Edge[]>
  sortedNodeEdges: AutomationsPresentationV1Edge[]
  branchResults: BuildResult[]
  branches: IntermediateNode[][]
  nodeId: string
  newVisited: Set<string>
  branchPath: string
  isInsideParallelFork: boolean
  outgoingEdges: Map<string, AutomationsPresentationV1Edge[]>
  joinNodeIds: Set<string>
  incomingSources: Map<string, Set<string>>
  buildStructure: BuildStructureFn
}): PerGroupJoinResult => {
  const edgeHandles = sortedNodeEdges.map(
    edge =>
      parseNodeHandleId(edge.sourceHandle ?? '').handleMapping ?? 'default'
  )

  const parallelHandleGroups = [...edgesByHandle.entries()].filter(
    ([, edges]) => edges.length > 1
  )

  return parallelHandleGroups.reduce<PerGroupJoinResult>(
    (acc, [handle]) => {
      const branchIndices = edgeHandles.reduce<number[]>(
        (indices, h, i) => (h === handle ? [...indices, i] : indices),
        []
      )

      const groupSkipped = new Set(
        branchIndices.flatMap(i => [...branchResults[i].skippedJoinNodes])
      )

      if (groupSkipped.size === 0) return acc

      const groupBranchTargets = new Set(
        branchIndices.map(i => sortedNodeEdges[i].target)
      )
      const groupBranchNodes = branchIndices.map(i => branches[i])

      const { continuation, remainingSkipped } = resolveJoinNodes({
        forkNodeId: nodeId,
        branchTargets: groupBranchTargets,
        skippedNodeIds: groupSkipped,
        visitedSoFar: new Set([...newVisited, ...acc.groupVisitedNodes]),
        branchNodes: groupBranchNodes,
        branchPath,
        isInsideParallelFork,
        outgoingEdges,
        joinNodeIds,
        incomingSources,
        buildStructure
      })

      const newResolved = [...groupSkipped].filter(
        id => !remainingSkipped.has(id)
      )

      return {
        groupContinuations:
          continuation.length > 0
            ? { ...acc.groupContinuations, [handle]: continuation }
            : acc.groupContinuations,
        groupResolvedIds: new Set([...acc.groupResolvedIds, ...newResolved]),
        groupRemainingSkipped: new Set([
          ...acc.groupRemainingSkipped,
          ...remainingSkipped
        ]),
        groupVisitedNodes: new Set([
          ...acc.groupVisitedNodes,
          ...getBaseNodeIds(continuation)
        ])
      }
    },
    {
      groupContinuations: {},
      groupResolvedIds: new Set(),
      groupRemainingSkipped: new Set(),
      groupVisitedNodes: new Set()
    }
  )
}
